/**
 * Two-factor authentication for the signed-in account.
 *
 * This route sits outside AppShell on purpose. When the operator has turned on
 * `require_mfa`, rooms and the socket answer 403 until enrolment finishes, so
 * the page that collects the first TOTP code cannot itself depend on them.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import type { FormEvent, ReactElement } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { needsMfaEnrollment, useAuth } from '@/api/auth'
import { api, isApiError } from '@/api/client'
import { queryKeys } from '@/api/queries'
import type {
  CurrentUser,
  MfaEnableResponse,
  MfaSetup,
  MfaStatus,
} from '@/api/types'
import { Button, TextField } from '@/components/ui'

import {
  AuthBrand,
  AuthCard,
  AuthFooter,
  AuthForm,
  AuthHeading,
  AuthNotice,
  AuthShell,
  describeRetry,
  networkMessage,
} from './LoginPage'
import page from './LoginPage.module.css'
import styles from './AccountPage.module.css'

function groupSecret(secret: string): string {
  return secret.replace(/(.{4})/g, '$1 ').trim()
}

async function copyText(value: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(value)
    return true
  } catch {
    return false
  }
}

function mfaErrorMessage(error: unknown): string {
  if (!isApiError(error)) return networkMessage()

  switch (error.code) {
    case 'invalid_mfa_code':
      return 'That code is not correct. Wait for a new one, or use a recovery code.'
    case 'invalid_credentials':
      return 'That password is not correct.'
    case 'mfa_required_by_server':
      return 'This server requires two-factor authentication, so it cannot be turned off.'
    case 'already_exists':
      return 'Two-factor authentication is already on for this account.'
    case 'rate_limited':
      return `Too many attempts. Try again ${describeRetry(error.retryAfterSeconds)}.`
    default:
      return error.message
  }
}

function StatusBody({
  status,
  required,
  onStarted,
}: {
  status: MfaStatus
  required: boolean
  onStarted: (setup: MfaSetup) => void
}): ReactElement {
  const queryClient = useQueryClient()
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [mode, setMode] = useState<'idle' | 'disable' | 'recovery'>('idle')
  const [freshCodes, setFreshCodes] = useState<string[] | null>(null)
  const [copied, setCopied] = useState(false)

  const startSetup = async (): Promise<void> => {
    setBusy(true)
    setError(null)
    try {
      onStarted(await api.post<MfaSetup>('/auth/mfa/setup'))
    } catch (caught) {
      setError(mfaErrorMessage(caught))
    } finally {
      setBusy(false)
    }
  }

  const submitSensitive = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault()
    if (mode === 'idle') return
    setBusy(true)
    setError(null)
    setCopied(false)

    try {
      if (mode === 'disable') {
        const updated = await api.del<CurrentUser>('/auth/mfa', { password, code })
        queryClient.setQueryData<CurrentUser>(queryKeys.me(), updated)
        await queryClient.invalidateQueries({ queryKey: queryKeys.mfa() })
        setMode('idle')
        setPassword('')
        setCode('')
      } else {
        const body = await api.post<{ recovery_codes: string[] }>('/auth/mfa/recovery', {
          password,
          code,
        })
        setFreshCodes(body.recovery_codes)
        setPassword('')
        setCode('')
        await queryClient.invalidateQueries({ queryKey: queryKeys.mfa() })
      }
    } catch (caught) {
      setError(mfaErrorMessage(caught))
    } finally {
      setBusy(false)
    }
  }

  if (!status.enabled) {
    return (
      <>
        <AuthHeading
          title={required ? 'Set up two-factor authentication' : 'Two-factor authentication'}
          lede={
            required
              ? 'This server requires an authenticator app before you can read messages. Copy the setup key into any TOTP app, then enter the six-digit code it shows.'
              : 'Add an authenticator app so a stolen password is not enough to read this server.'
          }
          primary
        />
        {error === null ? null : <AuthNotice tone="error">{error}</AuthNotice>}
        <Button variant="primary" fullWidth loading={busy} onClick={() => void startSetup()}>
          Set up authenticator
        </Button>
      </>
    )
  }

  if (freshCodes !== null) {
    return (
      <div className={styles.stack}>
        <AuthHeading
          title="New recovery codes"
          lede="These replace every unused code. Store them somewhere you can reach if you lose the phone."
          primary
        />
        <RecoveryCodes
          codes={freshCodes}
          copied={copied}
          onCopy={async () => {
            const ok = await copyText(freshCodes.join('\n'))
            setCopied(ok)
          }}
        />
        <Button
          variant="primary"
          fullWidth
          onClick={() => {
            setFreshCodes(null)
            setMode('idle')
            setCopied(false)
          }}
        >
          Done
        </Button>
      </div>
    )
  }

  return (
    <div className={styles.stack}>
      <AuthHeading
        title="Two-factor authentication"
        lede={
          required
            ? 'This server requires it. An administrator can clear the factor if you lose both the authenticator and the recovery codes.'
            : 'A code from your authenticator app is required each time you sign in.'
        }
        primary
      />

      <p className={page.lede}>
        {status.remaining_recovery_codes} unused recovery code
        {status.remaining_recovery_codes === 1 ? '' : 's'} remaining
        {status.enabled_at === null ? '' : `. On since ${status.enabled_at.slice(0, 10)}.`}
      </p>

      {error === null ? null : <AuthNotice tone="error">{error}</AuthNotice>}

      {mode === 'idle' ? (
        <div className={styles.stack}>
          <Button
            variant="secondary"
            fullWidth
            onClick={() => {
              setMode('recovery')
              setError(null)
            }}
          >
            Replace recovery codes
          </Button>
          {required ? (
            <AuthNotice tone="info">
              Two-factor authentication cannot be turned off while this server requires it.
            </AuthNotice>
          ) : (
            <Button
              variant="secondary"
              fullWidth
              onClick={() => {
                setMode('disable')
                setError(null)
              }}
            >
              Turn off two-factor authentication
            </Button>
          )}
        </div>
      ) : (
        <AuthForm onSubmit={(event) => void submitSensitive(event)}>
          <TextField
            label="Current password"
            name="current-password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
          />
          <TextField
            label={mode === 'recovery' ? 'Authenticator code' : 'Authenticator or recovery code'}
            name="one-time-code"
            autoComplete="one-time-code"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            value={code}
            onChange={(event) => setCode(event.target.value)}
            required
          />
          <div className={page.actions}>
            <Button type="submit" variant="primary" fullWidth loading={busy}>
              {mode === 'recovery' ? 'Issue new recovery codes' : 'Turn off'}
            </Button>
            <button
              type="button"
              className={page.linkButton}
              onClick={() => {
                setMode('idle')
                setPassword('')
                setCode('')
                setError(null)
              }}
            >
              Cancel
            </button>
          </div>
        </AuthForm>
      )}
    </div>
  )
}

function RecoveryCodes({
  codes,
  copied,
  onCopy,
}: {
  codes: readonly string[]
  copied: boolean
  onCopy: () => void | Promise<void>
}): ReactElement {
  return (
    <div className={styles.stack}>
      <ul className={styles.recoveryList}>
        {codes.map((item) => (
          <li key={item} className={styles.recoveryCode}>
            {item}
          </li>
        ))}
      </ul>
      <Button variant="secondary" fullWidth onClick={() => void onCopy()}>
        Copy all codes
      </Button>
      {copied ? (
        <p className={styles.copied} role="status">
          Copied. They will not be shown again.
        </p>
      ) : null}
    </div>
  )
}

function SetupBody({
  setup,
  onEnabled,
  onCancel,
}: {
  setup: MfaSetup
  onEnabled: (result: MfaEnableResponse) => void
  onCancel: () => void
}): ReactElement {
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const field = useRef<HTMLInputElement>(null)

  useEffect(() => {
    field.current?.focus()
  }, [])

  const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault()
    setError(null)

    if (code.trim() === '') {
      setError('Enter the six-digit code from the authenticator app.')
      return
    }

    setBusy(true)
    try {
      onEnabled(await api.post<MfaEnableResponse>('/auth/mfa/enable', { code: code.trim() }))
    } catch (caught) {
      setError(mfaErrorMessage(caught))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={styles.stack}>
      <AuthHeading
        title="Add this account"
        lede="Enter the setup key in your authenticator app, then type the six-digit code it shows."
        primary
      />

      <p className={styles.secret} aria-label="Authenticator setup key">
        {groupSecret(setup.secret)}
      </p>
      <Button
        variant="secondary"
        fullWidth
        onClick={() => {
          void copyText(setup.secret).then(setCopied)
        }}
      >
        Copy setup key
      </Button>
      {copied ? (
        <p className={styles.copied} role="status">
          Copied. Paste it into the authenticator as a time-based key.
        </p>
      ) : null}

      {error === null ? null : <AuthNotice tone="error">{error}</AuthNotice>}

      <AuthForm onSubmit={(event) => void submit(event)}>
        <TextField
          ref={field}
          label="Authenticator code"
          name="one-time-code"
          autoComplete="one-time-code"
          inputMode="numeric"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          value={code}
          onChange={(event) => setCode(event.target.value)}
          required
        />
        <div className={page.actions}>
          <Button type="submit" variant="primary" fullWidth loading={busy}>
            Turn on
          </Button>
          <button type="button" className={page.linkButton} onClick={onCancel}>
            Cancel
          </button>
        </div>
      </AuthForm>
    </div>
  )
}

export function AccountPage(): ReactElement {
  const { user, server, loading, logout, refresh } = useAuth()
  const queryClient = useQueryClient()
  const required = needsMfaEnrollment(user, server)
  const [setup, setSetup] = useState<MfaSetup | null>(null)
  const [recovery, setRecovery] = useState<string[] | null>(null)
  const [copied, setCopied] = useState(false)

  const statusQuery = useQuery({
    queryKey: queryKeys.mfa(),
    queryFn: ({ signal }) => api.get<MfaStatus>('/auth/mfa', undefined, signal),
    enabled: user !== null,
  })

  const onEnabled = useCallback(
    async (result: MfaEnableResponse): Promise<void> => {
      setSetup(null)
      setRecovery(result.recovery_codes)
      setCopied(false)
      queryClient.setQueryData<CurrentUser>(queryKeys.me(), result.user)
      queryClient.setQueryData<MfaStatus>(queryKeys.mfa(), {
        enabled: true,
        pending: false,
        enabled_at: result.user.mfa_enabled === true ? new Date().toISOString() : null,
        remaining_recovery_codes: result.recovery_codes.length,
      })
      await refresh()
      await queryClient.invalidateQueries({ queryKey: queryKeys.mfa() })
    },
    [queryClient, refresh],
  )

  if (loading) {
    return (
      <AuthShell>
        <AuthCard>
          <p className={page.lede}>Loading…</p>
        </AuthCard>
      </AuthShell>
    )
  }

  if (user === null) return <Navigate to="/login" replace />

  const serverName = server?.display_name ?? server?.server_id ?? null

  return (
    <AuthShell wide>
      <AuthBrand logoUrl={server?.logo_url ?? null} name={serverName} primary />

      <AuthCard>
        {recovery !== null ? (
          <div className={styles.stack}>
            <AuthHeading
              title="Save these recovery codes"
              lede="They are the only way back in if you lose the authenticator. relaii cannot show them again."
              primary
            />
            <RecoveryCodes
              codes={recovery}
              copied={copied}
              onCopy={async () => {
                const ok = await copyText(recovery.join('\n'))
                setCopied(ok)
              }}
            />
            <Button
              variant="primary"
              fullWidth
              onClick={() => {
                setRecovery(null)
              }}
            >
              I have saved these codes
            </Button>
          </div>
        ) : setup !== null ? (
          <SetupBody
            setup={setup}
            onEnabled={(result) => void onEnabled(result)}
            onCancel={() => setSetup(null)}
          />
        ) : statusQuery.isPending ? (
          <p className={page.lede}>Loading two-factor status…</p>
        ) : statusQuery.isError ? (
          <AuthNotice tone="error">{mfaErrorMessage(statusQuery.error)}</AuthNotice>
        ) : statusQuery.data === undefined ? null : (
          <StatusBody
            status={statusQuery.data}
            required={required}
            onStarted={setSetup}
          />
        )}
      </AuthCard>

      <AuthFooter>
        {required || recovery !== null ? (
          <button type="button" className={page.linkButton} onClick={() => void logout()}>
            Sign out
          </button>
        ) : (
          <>
            <Link to="/">Back to your rooms</Link>
            {' · '}
            <button type="button" className={page.linkButton} onClick={() => void logout()}>
              Sign out
            </button>
          </>
        )}
      </AuthFooter>
    </AuthShell>
  )
}